/**
 * Buyer-Demand Phase 3 — admin quota progress panel.
 * Phase 4 addition: in-flight count in the summary row.
 *
 * Read-only counterpart to QuotaBuilder. Shows live filled-vs-target
 * for each cell on a manual project, plus a summary row.
 *
 * Auto-refreshes every 30 s while the project is `LIVE` (the case where
 * progress changes faster than admin attention). Manual "Refresh" button
 * forces an immediate refetch regardless of state.
 *
 * Phase 4 — in-flight is read from the shared stats query
 * (StatsPanel owns the 15 s polling on the same queryKey). We subscribe
 * passively — staleTime: Infinity + refetchInterval: false — so this
 * component never fires its own duplicate request. If StatsPanel hasn't
 * populated the cache yet (or isn't mounted), the in-flight indicator
 * simply doesn't render. Picked over prop-drilling via getQueryData
 * (non-reactive) or sibling-callback plumbing (parent state churn).
 */
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  fetchProjectStats,
  fetchQuotaProgress,
  type ProjectStats,
  type QuotaProgressRow,
} from '../lib/adminApi';

interface Props {
  surveyId: string;
  isActive: boolean;
}

export default function QuotaProgress({ surveyId, isActive }: Props) {
  const query = useQuery({
    queryKey: ['admin', 'manual-project', surveyId, 'quota-progress'],
    queryFn: () => fetchQuotaProgress(surveyId),
    refetchInterval: isActive ? 30_000 : false,
    enabled: !!surveyId,
  });

  // Passive subscription to StatsPanel's stats cache — same queryKey
  // means we share the data without firing a second request. StatsPanel
  // (or whichever consumer mounts first) owns the actual fetch + 15s
  // polling. If neither component is mounted to drive a fetch, this
  // returns data: undefined and the in-flight indicator hides.
  const statsQuery = useQuery<ProjectStats>({
    queryKey: ['admin', 'manual-project', surveyId, 'stats'],
    queryFn: () => fetchProjectStats(surveyId),
    enabled: !!surveyId,
    refetchInterval: false,
    staleTime: Infinity,
  });
  const inFlight = statsQuery.data?.in_flight;

  const rows: QuotaProgressRow[] = query.data?.progress ?? [];

  const totals = rows.reduce(
    (acc, r) => ({ filled: acc.filled + r.filled, target: acc.target + r.target }),
    { filled: 0, target: 0 },
  );
  const overallPct = totals.target > 0
    ? Math.round((totals.filled / totals.target) * 1000) / 10
    : 0;

  return (
    <div className="bg-slate-800 rounded-xl p-6 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-white">Quota progress</p>
          <p className="text-xs text-slate-400 mt-1">
            Live counter rows. Updates every 30 seconds while the project is live.
          </p>
        </div>
        <button
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${query.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : query.isError ? (
        <p className="text-sm text-red-400">
          {query.error instanceof Error ? query.error.message : 'Failed to load progress'}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">
          No quotas defined — every qualifying complete is credited.
        </p>
      ) : (
        <>
          <ul className="space-y-2">
            {rows.map((row) => (
              <ProgressRow key={row.quota_key} row={row} />
            ))}
          </ul>

          <div className="pt-3 border-t border-slate-700 flex items-center gap-4 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-slate-400">Total</span>
            <span className="text-sm text-white">
              <span className="font-bold tabular-nums">{totals.filled.toLocaleString()}</span>
              <span className="text-slate-500"> / {totals.target.toLocaleString()}</span>
            </span>
            <span className="text-xs text-slate-500">({overallPct.toFixed(1)}%)</span>
            {inFlight !== undefined && inFlight > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-300 ml-auto">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" aria-hidden />
                <span className="tabular-nums font-medium">{inFlight.toLocaleString()}</span>
                <span className="text-amber-300/80">in progress</span>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ProgressRow({ row }: { row: QuotaProgressRow }) {
  const pct = Math.min(100, Math.max(0, row.pct_filled));
  const full = row.filled >= row.target;
  const constraintText = describeConstraints(row.constraints);

  return (
    <li className="bg-slate-900 rounded-lg p-3 border border-slate-700">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-white font-medium truncate" title={constraintText}>
          {constraintText || <span className="text-slate-500 font-mono">{row.quota_key}</span>}
        </p>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-sm text-white tabular-nums">
            <span className={full ? 'text-green-400 font-bold' : 'font-bold'}>
              {row.filled.toLocaleString()}
            </span>
            <span className="text-slate-500"> / {row.target.toLocaleString()}</span>
          </span>
          <span className="text-xs text-slate-500 tabular-nums w-12 text-right">
            {row.pct_filled.toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${full ? 'bg-green-500' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </li>
  );
}

/**
 * Render `{ gender: 'female', age_range: '18-34' }` as
 * "Gender: female · Age range: 18-34". Falls back to the raw key when
 * we can't humanize. Keeps order alphabetical (matches the quota_key
 * generator) so display is stable.
 */
function describeConstraints(constraints: Record<string, string>): string {
  const keys = Object.keys(constraints).sort();
  if (keys.length === 0) return '';
  return keys
    .map((k) => `${humanize(k)}: ${constraints[k]}`)
    .join(' · ');
}

function humanize(key: string): string {
  return key
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}
